import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "../../utils";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  "af-inline-flex af-items-center af-justify-center af-gap-2 af-whitespace-nowrap af-rounded-md af-text-sm af-font-medium af-ring-offset-background af-transition-colors focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2 disabled:af-pointer-events-none disabled:af-opacity-50 [&_svg:not([class*=size-])]:af-size-4 [&_svg]:af-pointer-events-none [&_svg]:af-shrink-0",
  {
    variants: {
      variant: {
        default:
          "af-bg-primary af-text-primary-foreground hover:af-bg-primary/90",
        destructive:
          "af-bg-destructive af-text-destructive-foreground hover:af-bg-destructive/90",
        outline:
          "af-border af-border-input af-bg-background hover:af-bg-accent hover:af-text-foreground",
        secondary:
          "af-bg-secondary af-text-secondary-foreground hover:af-bg-secondary/80",
        ghost: "hover:af-bg-accent hover:af-text-accent-foreground",
        link: "af-text-primary af-underline-offset-4 hover:af-underline",
        success:
          "af-border af-border-success-accent af-bg-success af-text-success-foreground hover:af-bg-success-accent/20",
      },
      size: {
        default: "af-h-10 af-px-4 af-py-2",
        xs: "af-px-2 af-py-1 af-text-xs",
        sm: "af-h-9 af-rounded-md af-px-3",
        lg: "af-h-11 af-rounded-md af-px-8",
        icon: "af-h-10 af-w-10",
        "icon-sm": "af-h-9 af-w-9",
        "icon-xs": "af-h-6 af-w-6",
      },
      selected: {
        true: "",
      },
    },
    compoundVariants: [
      {
        variant: "success",
        selected: true,
        className:
          "hover:af-bg-success-accent/90 [&]:af-bg-success-accent [&]:af-text-primary-foreground",
      },
      {
        variant: "outline",
        selected: true,
        className:
          "hover:af-bg-secondary/10 hover:af-text-primary [&]:af-border-primary [&]:af-bg-secondary/20 [&]:af-text-primary",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      selected: false,
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

function Button({
  className,
  variant,
  size,
  selected,
  asChild = false,
  loading,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, selected, className }))}
      disabled={loading}
      {...props}
    >
      {loading && <Spinner />}
      <Slottable>{children}</Slottable>
    </Comp>
  );
}
Button.displayName = "Button";

export { Button, buttonVariants };
